import { BigInteger } from 'jsbn';

import { Buffer } from 'buffer';
import * as crypto from 'crypto-browserify';

const createHash = crypto.createHash;
const randomBytes = crypto.randomBytes;

if (typeof BigInteger.ZERO === 'undefined') {
    BigInteger.ZERO = new BigInteger('0');
}

export class SRPHelper {
    constructor(constantN, constantG, constantK, salt) {
        this.N = new BigInteger(constantN, 16);
        this.g = new BigInteger(constantG, 16);
        this.k = new BigInteger(constantK, 16);

        this.smallAValue = this.generateRandomSmallA();
        this.largeAValue = this.calculateA(this.smallAValue);
        this.salt = salt || this.generateSalt();
    }

    /**
     * helper function to generate a random big integer
     * @returns {BigInteger} a random value.
     * @private
     */
    generateRandomSmallA() {
        let a = randomBytes(128);
        a = a.toString('hex');
        a = new BigInteger(a, 16);
        a = a.mod(this.N);

        return a;
    }

    /**
     * Calculate the client's public value A = g^a%N
     * with the generated random number a
     * @param {BigInteger} a Randomly generated small A.
     * @private
     */
    calculateA(a) {
        let A = this.g.modPow(a, this.N, () => {});
        if (A.mod(this.N).equals(BigInteger.ZERO)) {
            return null;
        }
        return A;
    }

    /**
     * helper function to generate a random string
     * @returns {string} a random value.
     * @private
     */
    generateRandomString() {
        return randomBytes(40).toString('base64');
    }

    generateSalt() {
        return this.padHex(new BigInteger(randomBytes(16).toString('hex'), 16));
    }

    calculateX(username, password, salt) {
        let x = `${username}:${password}`;
        x = this.hash(Buffer.from(x, 'utf8'));
        x = salt + x;
        x = this.hexHash(x);
        x = new BigInteger(x, 16);
        return x;
    }

    /**
     * Generate salts and compute verifier.
     * @param {string} username Username to generate verifier for.
     * @param {string} password Password to generate verifier for.
     */
    generateVerifier(username, password) {
        password = this.convertPassword(username, password);
        let x = this.calculateX(username, password, this.salt);
        this.verifier = this.g.modPow(x, this.N, () => {});
        this.verifier = this.padHex(this.verifier);
        return this.verifier;
    }

    convertPassword(username, password) {
        let value = username + password;
        let hasher = createHash('md5');
        hasher.update(value);
        value = hasher.digest('hex');
        return value;
    }

    /**
     * Calculate the client's value U which is the hash of A and B
     * @param {BigInteger} A Large A value.
     * @param {BigInteger} B Server B value.
     * @returns {BigInteger} Computed U value.
     * @private
     */
    calculateU(A, B) {
        let U = this.hexHash(this.padHex(A) + this.padHex(B));
        U = new BigInteger(U, 16);

        return U;
    }

    /**
     * Calculate a hash from a bitArray
     * @param {Buffer} buf Value to hash.
     * @returns {String} Hex-encoded hash.
     * @private
     */
    hash(value) {
        let hasher = createHash('sha256');
        hasher.update(value);
        value = hasher.digest('hex');
        value = new Array(64 - value.length).join('0') + value;
        return value;
    }

    /**
     * Calculate a hash from a hex string
     * @param {String} hexStr Value to hash.
     * @returns {String} Hex-encoded hash.
     * @private
     */
    hexHash(value) {
        return this.hash(Buffer.from(value, 'hex'));
    }

    /**
     * Calculates the final hkdf based on computed S value, and computed U value and the key
     * @param {String} username Username.
     * @param {String} password Password.
     * @param {BigInteger} serverBValue Server B value.
     * @param {BigInteger} salt Generated salt
     */
    calculateProof(username, password, serverBValue) {
        /*
          x = H(s, I, p)
          S_c = pow(B - k * pow(g, x, N), a + u * x, N)
        */

        serverBValue = new BigInteger(serverBValue, 16);
        if (serverBValue.mod(this.N).equals(BigInteger.ZERO)) {
            throw new Error('B cannot be zero.');
        }

        password = this.convertPassword(username, password);
        const xValue = this.calculateX(username, password, this.salt);
        const uValue = this.calculateU(this.largeAValue, serverBValue);

        let exponent = uValue.multiply(xValue);
        exponent = exponent.add(this.smallAValue);

        let base = this.g.modPow(xValue, this.N, () => {});
        base = base.multiply(this.k);
        base = serverBValue.subtract(base);
        base = base.mod(this.N);

        let calculated = base.modPow(exponent, this.N, () => {});
        return calculated;
    }

    /**
     * Converts a BigInteger (or hex string) to hex format padded with zeroes for hashing
     * @param {BigInteger|String} bigInt Number or string to pad.
     * @returns {String} Padded hex string.
     */
    padHex(value) {
        value = value.toString(16);
        if (value.length % 2 === 1) {
            value = '0' + value;
        } else if ('89ABCDEFabcdef'.indexOf(value[0]) !== -1) {
            value = '00' + value;
        }
        return value;
    }
}
